import type { RxDocument } from 'rxdb';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { Note } from '@flintmint/core-shared/rxdb';

type UserSourceLinkMap = Record<string, { enabled: boolean; index: number }>;

export const useNoteUserSourceActions = ({
  note,
  noteId,
  queryNote,
  upsertNote,
  onCurrentNoteSaved,
}: {
  noteId?: string;
  note?: RxDocument<Note>;
  upsertNote: (payload: Partial<Note>) => Promise<RxDocument<Note>>;
  queryNote: (id: string) => Promise<RxDocument<Note> | null>;
  onCurrentNoteSaved: (savedNoteId: string, targetNoteId: string) => void;
}) => {
  const queue = useMemo(() => {
    let queue = Promise.resolve();

    return {
      enqueue: <T>(task: () => Promise<T>): Promise<T> => {
        const run = queue.then(task, task);
        queue = run.then(
          () => undefined,
          () => undefined
        );
        return run;
      },
    };
  }, []);

  // `upsertNote` closes over the note document, so it is replaced on every autosave, and
  // `onCurrentNoteSaved` is usually an inline callback. Depending on them directly would give the
  // returned actions a new identity per save, re-rendering every source row. They are only invoked
  // from a user action, long after the effect below has committed, so read them through refs at call
  // time and keep the actions stable for the note's lifetime.
  const latest = useRef({ note, upsertNote, queryNote, onCurrentNoteSaved });
  useEffect(() => {
    latest.current = { note, upsertNote, queryNote, onCurrentNoteSaved };
  });

  const enqueueUserSourcesUpdate = useCallback(
    async (updateSources: (current: UserSourceLinkMap) => UserSourceLinkMap) => {
      if (!noteId) return;

      const targetNoteId = noteId;
      // Read at enqueue time, not inside the task: an operation started on this note must finish
      // against this note's writer even if the user navigates away before the queue drains.
      const { note: enqueuedNote, upsertNote: upsert, queryNote: query, onCurrentNoteSaved: onSaved } = latest.current;
      const fallbackUserSources = enqueuedNote?.toJSON().userSources || {};

      await queue.enqueue(async () => {
        const targetNote = await query(targetNoteId);
        const currentUserSources = targetNote?.toJSON().userSources || fallbackUserSources;
        const nextUserSources = updateSources(currentUserSources);
        const savedNote = await upsert({ userSources: nextUserSources });
        onSaved(savedNote.id, targetNoteId);
      });
    },
    [noteId, queue]
  );

  const onAttachUserSources = useCallback(
    async (userSourceIds: string[]) => {
      if (!userSourceIds.length) return;
      await enqueueUserSourcesUpdate((currentUserSources) => {
        const nextUserSources = { ...currentUserSources };
        userSourceIds.forEach((sourceId) => {
          nextUserSources[sourceId] = { enabled: true, index: Date.now() };
        });
        return nextUserSources;
      });
    },
    [enqueueUserSourcesUpdate]
  );

  const onDetachUserSources = useCallback(
    async (userSourceIds: string[]) => {
      if (!userSourceIds.length) return;
      await enqueueUserSourcesUpdate((currentUserSources) => {
        const nextUserSources = { ...currentUserSources };
        userSourceIds.forEach((sourceId) => {
          delete nextUserSources[sourceId];
        });
        return nextUserSources;
      });
    },
    [enqueueUserSourcesUpdate]
  );

  const onReplaceUserSource = useCallback(
    async (attachedUserSourceId: string, replacementUserSourceId: string) => {
      if (!attachedUserSourceId || !replacementUserSourceId) return;
      await enqueueUserSourcesUpdate((currentUserSources) => {
        const nextUserSources = { ...currentUserSources };
        delete nextUserSources[attachedUserSourceId];
        nextUserSources[replacementUserSourceId] = { enabled: true, index: Date.now() };
        return nextUserSources;
      });
    },
    [enqueueUserSourcesUpdate]
  );

  const onUserSourceEnabledChange = useCallback(
    async (userSourceId: string, enabled: boolean) => {
      if (!userSourceId) return;
      await enqueueUserSourcesUpdate((currentUserSources) => {
        const existingSource = currentUserSources[userSourceId];
        if (!existingSource) {
          return currentUserSources;
        }

        return {
          ...currentUserSources,
          [userSourceId]: {
            ...existingSource,
            enabled,
          },
        };
      });
    },
    [enqueueUserSourcesUpdate]
  );

  return { onAttachUserSources, onDetachUserSources, onReplaceUserSource, onUserSourceEnabledChange };
};
